import React, { Component } from 'react';
import { observable, action, computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import moment from 'moment';
import {
  Container, Row, Col, Card, CardBody, CardHeader,
  FormInput, FormTextarea, Button, FormSelect, Modal, ModalHeader, ModalBody
} from 'shards-react';
import FeatherIcon from 'feather-icons-react';

import PageTitle from '../../../components/common/PageTitle';
import ModalLoading from '../../../components/common/ModalLoading';

const DEFAULT_SENDER = '0264177000';
const SOURCE_LABEL_DIRECT = 'aligoSmsDirect';

// 모바일 번호 판별 — 010/011/016/017/018/019로 시작하고 총 10~11자리
const MOBILE_PREFIX_RE = /^(010\d{7,8}|01[16-9]\d{7,8})$/;
const isMobilePhone = (tel) => {
  if (!tel) return false;
  const digits = (tel + '').replace(/[^\d]/g, '');
  return MOBILE_PREFIX_RE.test(digits);
};

@inject(({ teamUser }) => ({ isLogin: teamUser.isLogin, me: teamUser.me }))
@inject(({ AOA }) => ({
  sendAligoSms: AOA.sendAligoSms,
  getStoreTelList: AOA.getStoreTelList,
  saveSmsTemplate: AOA.saveSmsTemplate,
  deleteSmsTemplate: AOA.deleteSmsTemplate,
  getSmsTemplateList: AOA.getSmsTemplateList,
  saveScheduledSms: AOA.saveScheduledSms,
  cancelScheduledSms: AOA.cancelScheduledSms,
  getScheduledSmsList: AOA.getScheduledSmsList,
  getSmsHistoryList: AOA.getSmsHistoryList,
}))
@observer
class AligoSmsSend extends Component {

  @observable isLogin = false;
  @observable teamUser = {};
  @observable isLoading = false;

  // 탭: compose | scheduled | history | templates
  @observable activeTab = 'compose';

  // 폼 상태
  @observable form = {
    sender: DEFAULT_SENDER,
    receiversText: '',
    message: '',
    msgType: 'AUTO',
    sendMode: 'now',     // 'now' | 'schedule'
    sendDate: '',        // YYYY-MM-DD
    sendTime: '',        // HH:mm
  };

  // 발송 결과
  @observable result = null;
  @observable lastError = '';

  // 스토어 가져오기 모달
  @observable storeModal = {
    isOpen: false,
    isLoading: false,
    filterType: 'paid',
    items: [],
    selectedIds: {},
  };

  // 템플릿 모달 (저장/불러오기 공용)
  @observable tplModal = { isOpen: false, mode: 'load', name: '' }; // mode: load | save

  // 데이터
  @observable templates = [];
  @observable scheduledList = { docs: [], totalDocs: 0, page: 1, totalPages: 1 };
  @observable historyList = { docs: [], totalDocs: 0, page: 1, totalPages: 1 };

  componentDidMount() {
    this.init();
  }

  @action init = () => {
    if (this.props.isLogin && this.props.me) {
      this.isLogin = true;
      this.teamUser = this.props.me;
      this.loadTemplates();
    }
  };

  // ===== computed =====

  @computed get parsedReceivers() {
    return (this.form.receiversText || '')
      .split(/[,;\s\n]+/)
      .map(p => p.replace(/[^\d]/g, ''))
      .filter(p => p && p.length >= 9);
  }

  @computed get duplicatedReceivers() {
    const counts = {};
    this.parsedReceivers.forEach(p => { counts[p] = (counts[p] || 0) + 1; });
    return Object.keys(counts).filter(p => counts[p] > 1);
  }

  @computed get uniqueReceivers() {
    return Array.from(new Set(this.parsedReceivers));
  }

  @computed get computedMsgType() {
    if (this.form.msgType === 'AUTO') {
      return (this.form.message || '').length > 90 ? 'LMS' : 'SMS';
    }
    return this.form.msgType;
  }

  @computed get messageLength() { return (this.form.message || '').length; }

  @computed get scheduledAt() {
    if (this.form.sendMode !== 'schedule') return null;
    if (!this.form.sendDate || !this.form.sendTime) return null;
    const dt = moment(`${this.form.sendDate} ${this.form.sendTime}`, 'YYYY-MM-DD HH:mm');
    return dt.isValid() ? dt.toDate() : null;
  }

  @computed get canSend() {
    if (this.isLoading) return false;
    if (!this.form.sender) return false;
    if (!this.form.message) return false;
    if (!this.uniqueReceivers.length) return false;
    if (this.form.sendMode === 'schedule') {
      if (!this.scheduledAt) return false;
      if (this.scheduledAt.getTime() < Date.now() - 60 * 1000) return false;
    }
    return true;
  }

  // ===== actions =====

  @action onChangeForm = (key, value) => { this.form[key] = value; };

  @action setTab = (key) => {
    this.activeTab = key;
    if (key === 'scheduled') this.loadScheduled();
    else if (key === 'history') this.loadHistory();
    else if (key === 'templates') this.loadTemplates();
  };

  @action onSubmit = async () => {
    if (!this.canSend) return;

    const senderClean = this.form.sender.replace(/[^\d]/g, '');
    if (!senderClean || senderClean.length < 9) {
      toast.error('발신번호 형식이 올바르지 않습니다.');
      return;
    }
    if (this.uniqueReceivers.length > 1000) {
      toast.error('한 번에 최대 1000명까지 발송할 수 있습니다.');
      return;
    }

    if (this.form.sendMode === 'schedule') {
      const at = moment(this.scheduledAt).format('YYYY-MM-DD HH:mm');
      if (!window.confirm(`${at} 시각에 ${this.uniqueReceivers.length}명 예약 발송을 등록합니다. 계속하시겠습니까?`)) return;
      this.isLoading = true;
      try {
        const res = await this.props.saveScheduledSms({
          sender: senderClean,
          receivers: this.uniqueReceivers,
          message: this.form.message,
          msgType: this.form.msgType,
          sendAt: this.scheduledAt.toISOString(),
        });
        if (res && res.data && res.data.error) {
          toast.error(`예약 실패: ${res.data.error}`);
        } else {
          toast.success('예약 등록 완료');
          this.clearForm();
          this.setTab('scheduled');
        }
      } catch (e) {
        toast.error('예약 중 오류가 발생했습니다.');
      } finally {
        this.isLoading = false;
      }
      return;
    }

    // 즉시 발송
    const confirmMsg = `${this.uniqueReceivers.length}명에게 ${this.computedMsgType} 문자를 발송합니다. 진행하시겠습니까?`;
    if (!window.confirm(confirmMsg)) return;

    this.isLoading = true;
    this.lastError = '';
    this.result = null;
    try {
      const res = await this.props.sendAligoSms({
        sender: senderClean,
        receivers: this.uniqueReceivers,
        message: this.form.message,
        msgType: this.form.msgType === 'AUTO' ? null : this.form.msgType,
        sourceLabel: SOURCE_LABEL_DIRECT,
      });
      if (res && res.data && res.data.error) {
        this.lastError = res.data.error;
        toast.error(`발송 실패: ${res.data.error}`);
      } else if (res && res.data) {
        this.result = res.data;
        const apiResult = res.data.apiResult || {};
        if (apiResult.result_code !== undefined && Number(apiResult.result_code) < 0) {
          this.lastError = apiResult.message || '알리고 API 응답 오류';
          toast.error(`알리고 API 오류: ${this.lastError}`);
        } else {
          toast.success(`${res.data.recipientCount}명에게 발송 요청을 완료했습니다.`);
        }
      } else {
        this.lastError = '알 수 없는 응답';
        toast.error('알 수 없는 응답입니다.');
      }
    } catch (e) {
      this.lastError = e.message || String(e);
      toast.error('발송 중 오류가 발생했습니다.');
    } finally {
      this.isLoading = false;
    }
  };

  @action clearForm = () => {
    this.form = {
      sender: this.form.sender || DEFAULT_SENDER,
      receiversText: '',
      message: '',
      msgType: 'AUTO',
      sendMode: 'now',
      sendDate: '',
      sendTime: '',
    };
    this.result = null;
    this.lastError = '';
  };

  // ===== 스토어 가져오기 =====

  @action openStoreModal = () => {
    this.storeModal = { isOpen: true, isLoading: false, filterType: 'paid', items: [], selectedIds: {} };
    this.fetchStoreTel();
  };

  @action closeStoreModal = () => { this.storeModal.isOpen = false; };

  @action fetchStoreTel = async () => {
    this.storeModal.isLoading = true;
    try {
      const res = await this.props.getStoreTelList({
        filterType: this.storeModal.filterType,
        limit: 1000,
      });
      if (res && res.data && res.data.error) {
        toast.error(res.data.error);
        this.storeModal.items = [];
      } else if (res && res.data && res.data.items) {
        this.storeModal.items = res.data.items;
        // 기본 전체 선택 — 모바일 번호만
        const all = {};
        res.data.items.forEach(it => { if (isMobilePhone(it.tel)) all[it.tel] = true; });
        this.storeModal.selectedIds = all;
      }
    } catch (e) {
      toast.error('스토어 조회 중 오류가 발생했습니다.');
    } finally {
      this.storeModal.isLoading = false;
    }
  };

  @action toggleStoreItem = (tel) => {
    if (!isMobilePhone(tel)) return;
    this.storeModal.selectedIds[tel] = !this.storeModal.selectedIds[tel];
  };

  @action toggleAllStoreItems = (allOn) => {
    const next = {};
    // 모바일 번호만 토글 대상
    this.storeModal.items.forEach(it => { if (isMobilePhone(it.tel)) next[it.tel] = !!allOn; });
    this.storeModal.selectedIds = next;
  };

  @action applyStoreSelection = () => {
    const tels = Object.keys(this.storeModal.selectedIds).filter(t => this.storeModal.selectedIds[t]);
    if (!tels.length) {
      toast.warn('선택된 번호가 없습니다.');
      return;
    }
    // 기존 textarea와 합치되 중복 제거
    const existing = (this.form.receiversText || '').split(/[,;\s\n]+/).map(p => p.replace(/[^\d]/g, '')).filter(Boolean);
    const merged = Array.from(new Set([...existing, ...tels]));
    this.form.receiversText = merged.join('\n');
    this.closeStoreModal();
    toast.success(`${tels.length}개 번호를 추가했습니다.`);
  };

  // ===== 템플릿 =====

  @action loadTemplates = async () => {
    try {
      const res = await this.props.getSmsTemplateList({ page: 1, limit: 100 });
      this.templates = (res && res.docs) || [];
    } catch (e) { /* ignore */ }
  };

  @action openTemplateModal = (mode) => {
    this.tplModal = { isOpen: true, mode, name: '' };
    if (mode === 'load') this.loadTemplates();
  };

  @action closeTemplateModal = () => { this.tplModal.isOpen = false; };

  @action saveCurrentAsTemplate = async () => {
    if (!this.tplModal.name) { toast.warn('템플릿 이름을 입력해 주세요.'); return; }
    if (!this.form.message) { toast.warn('메시지 내용을 먼저 입력해 주세요.'); return; }
    this.isLoading = true;
    try {
      const res = await this.props.saveSmsTemplate({
        data: {
          name: this.tplModal.name,
          sender: this.form.sender,
          message: this.form.message,
          msgType: this.form.msgType,
        }
      });
      if (res && res.data && res.data.error) {
        toast.error(res.data.error);
      } else {
        toast.success('템플릿이 저장되었습니다.');
        this.closeTemplateModal();
        await this.loadTemplates();
      }
    } catch (e) {
      toast.error('템플릿 저장 실패');
    } finally {
      this.isLoading = false;
    }
  };

  @action applyTemplate = (tpl) => {
    this.form.sender = tpl.sender || this.form.sender || DEFAULT_SENDER;
    this.form.message = tpl.message || '';
    this.form.msgType = tpl.msgType || 'AUTO';
    this.closeTemplateModal();
    toast.success(`"${tpl.name}" 템플릿을 불러왔습니다.`);
  };

  @action removeTemplate = async (tpl) => {
    if (!window.confirm(`"${tpl.name}" 템플릿을 삭제하시겠습니까?`)) return;
    try {
      await this.props.deleteSmsTemplate({ id: tpl._id });
      toast.success('삭제되었습니다.');
      this.loadTemplates();
    } catch (e) {
      toast.error('삭제 실패');
    }
  };

  // ===== 예약 목록 =====

  @action loadScheduled = async () => {
    try {
      const res = await this.props.getScheduledSmsList({ page: 1, limit: 100 });
      this.scheduledList = res || this.scheduledList;
    } catch (e) { /* ignore */ }
  };

  @action cancelScheduled = async (item) => {
    if (!window.confirm('예약을 취소하시겠습니까?')) return;
    try {
      const res = await this.props.cancelScheduledSms({ id: item._id });
      if (res && res.data && res.data.error) toast.error(res.data.error);
      else { toast.success('취소되었습니다.'); this.loadScheduled(); }
    } catch (e) { toast.error('취소 실패'); }
  };

  // ===== 이력 =====

  @action loadHistory = async () => {
    try {
      const res = await this.props.getSmsHistoryList({ channel: 'sms', page: 1, limit: 50 });
      this.historyList = res || this.historyList;
    } catch (e) { /* ignore */ }
  };

  // ===== render helpers =====

  renderTabs() {
    const tabs = [
      { key: 'compose', label: '발송', icon: 'send' },
      { key: 'scheduled', label: '예약 목록', icon: 'clock' },
      { key: 'history', label: '발송 이력', icon: 'list' },
      { key: 'templates', label: '템플릿', icon: 'file-text' },
    ];
    return (
      <div style={{display: 'flex', gap: 4, borderBottom: '1px solid #E5E7EB', marginBottom: 0}}>
        {tabs.map(t => {
          const active = this.activeTab === t.key;
          return (
            <button key={t.key} type="button"
              onClick={() => this.setTab(t.key)}
              style={{
                padding: '10px 16px',
                fontSize: 13, fontWeight: active ? 700 : 500,
                color: active ? '#2563EB' : '#6B7280',
                background: active ? '#EFF6FF' : 'transparent',
                border: 'none',
                borderBottom: active ? '2px solid #2563EB' : '2px solid transparent',
                cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
              <FeatherIcon icon={t.icon} size="13"/>
              {t.label}
            </button>
          );
        })}
      </div>
    );
  }

  renderResult() {
    if (this.lastError) {
      return (
        <Card className="mt-3" style={{borderLeft: '4px solid #DC2626'}}>
          <CardBody>
            <h5 className="mb-2" style={{color: '#DC2626'}}>
              <FeatherIcon icon="x-circle" size="16" style={{verticalAlign: '-3px', marginRight: 6}}/>
              발송 실패
            </h5>
            <div style={{fontSize: 13, color: '#6B7280'}}>{this.lastError}</div>
          </CardBody>
        </Card>
      );
    }
    if (!this.result) return null;
    const apiResult = this.result.apiResult || {};
    return (
      <Card className="mt-3" style={{borderLeft: '4px solid #10B981'}}>
        <CardBody>
          <h5 className="mb-2" style={{color: '#10B981'}}>
            <FeatherIcon icon="check-circle" size="16" style={{verticalAlign: '-3px', marginRight: 6}}/>
            발송 요청 완료
          </h5>
          <div style={{fontSize: 13, lineHeight: 1.8}}>
            <div><strong>발송 대상:</strong> {this.result.recipientCount}명</div>
            <div><strong>메시지 타입:</strong> {this.result.msgType}</div>
            {apiResult.result_code !== undefined && (
              <div><strong>알리고 응답코드:</strong> {apiResult.result_code} ({apiResult.message || '-'})</div>
            )}
            {apiResult.success_cnt !== undefined && <div><strong>성공 건수:</strong> {apiResult.success_cnt}</div>}
            {apiResult.error_cnt !== undefined && <div><strong>실패 건수:</strong> {apiResult.error_cnt}</div>}
            {apiResult.msg_id && <div><strong>메시지 ID:</strong> {apiResult.msg_id}</div>}
          </div>
        </CardBody>
      </Card>
    );
  }

  renderCompose() {
    const dups = this.duplicatedReceivers;
    return (
      <CardBody>
        <Row>
          <Col md="6">
            <div className="mb-3">
              <label style={{fontWeight: 600, fontSize: 13}}>
                발신번호 <span style={{color: '#DC2626'}}>*</span>
              </label>
              <FormInput placeholder="01012345678" value={this.form.sender}
                onChange={e => this.onChangeForm('sender', e.target.value)}/>
              <small style={{color: '#6B7280', fontSize: 11}}>알리고에 사전 등록된 발신번호여야 합니다.</small>
            </div>
          </Col>
          <Col md="6">
            <div className="mb-3">
              <label style={{fontWeight: 600, fontSize: 13}}>메시지 타입</label>
              <FormSelect value={this.form.msgType} onChange={e => this.onChangeForm('msgType', e.target.value)}>
                <option value="AUTO">자동 (90자 이하 SMS / 초과 LMS)</option>
                <option value="SMS">SMS (단문)</option>
                <option value="LMS">LMS (장문)</option>
              </FormSelect>
              <small style={{color: '#6B7280', fontSize: 11}}>
                현재 적용: <strong>{this.computedMsgType}</strong>
              </small>
            </div>
          </Col>
        </Row>

        <div className="mb-3">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4}}>
            <label style={{fontWeight: 600, fontSize: 13, margin: 0}}>
              수신번호 <span style={{color: '#DC2626'}}>*</span>
              <span style={{marginLeft: 8, fontWeight: 400, fontSize: 12, color: '#6B7280'}}>
                (콤마 / 세미콜론 / 줄바꿈으로 여러 개 입력)
              </span>
            </label>
            <Button size="sm" theme="light" onClick={() => this.openStoreModal()}>
              <FeatherIcon icon="users" size="12" style={{verticalAlign: '-2px', marginRight: 4}}/>
              스토어에서 가져오기
            </Button>
          </div>
          <FormTextarea rows="6" placeholder={'01011112222\n01033334444\n01055556666'}
            value={this.form.receiversText}
            onChange={e => this.onChangeForm('receiversText', e.target.value)}
            style={{fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 12}}/>
          <div style={{display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 11}}>
            <span style={{color: '#6B7280'}}>
              유효 번호: <strong style={{color: '#10B981'}}>{this.uniqueReceivers.length}</strong>
              {dups.length > 0 && <span style={{marginLeft: 8, color: '#D97706'}}>중복 제거: {dups.length}개</span>}
            </span>
            {this.uniqueReceivers.length > 1000 && <span style={{color: '#DC2626'}}>1000명 초과</span>}
          </div>
        </div>

        <div className="mb-3">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4}}>
            <label style={{fontWeight: 600, fontSize: 13, margin: 0}}>
              메시지 내용 <span style={{color: '#DC2626'}}>*</span>
            </label>
            <div style={{display: 'flex', gap: 6}}>
              <Button size="sm" theme="light" onClick={() => this.openTemplateModal('load')}>
                <FeatherIcon icon="folder" size="12" style={{verticalAlign: '-2px', marginRight: 4}}/>
                템플릿 불러오기
              </Button>
              <Button size="sm" theme="light" onClick={() => this.openTemplateModal('save')}>
                <FeatherIcon icon="save" size="12" style={{verticalAlign: '-2px', marginRight: 4}}/>
                템플릿으로 저장
              </Button>
            </div>
          </div>
          <FormTextarea rows="6" placeholder="발송할 메시지 내용을 입력하세요."
            value={this.form.message}
            onChange={e => this.onChangeForm('message', e.target.value)}/>
          <div style={{textAlign: 'right', marginTop: 4, fontSize: 11, color: this.messageLength > 90 ? '#D97706' : '#6B7280'}}>
            {this.messageLength}자 / {this.computedMsgType}
            {this.messageLength > 2000 && <span style={{color: '#DC2626', marginLeft: 8}}>2000자 초과 (LMS 최대)</span>}
          </div>
        </div>

        <div className="mb-3">
          <label style={{fontWeight: 600, fontSize: 13}}>발송 시점</label>
          <div style={{display: 'flex', gap: 16, alignItems: 'center'}}>
            <label style={{display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer'}}>
              <input type="radio" checked={this.form.sendMode === 'now'} onChange={() => this.onChangeForm('sendMode', 'now')}/>
              즉시 발송
            </label>
            <label style={{display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer'}}>
              <input type="radio" checked={this.form.sendMode === 'schedule'} onChange={() => this.onChangeForm('sendMode', 'schedule')}/>
              예약 발송
            </label>
            {this.form.sendMode === 'schedule' && (
              <>
                <FormInput type="date" value={this.form.sendDate} style={{maxWidth: 160}}
                  onChange={e => this.onChangeForm('sendDate', e.target.value)}/>
                <FormInput type="time" value={this.form.sendTime} style={{maxWidth: 120}}
                  onChange={e => this.onChangeForm('sendTime', e.target.value)}/>
                {this.scheduledAt && (
                  <small style={{color: '#6B7280'}}>
                    {moment(this.scheduledAt).format('YYYY-MM-DD HH:mm')}
                  </small>
                )}
              </>
            )}
          </div>
        </div>

        <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #E5E7EB', paddingTop: 14}}>
          <Button theme="light" size="sm" onClick={() => this.clearForm()}>
            <FeatherIcon icon="rotate-ccw" size="13" style={{verticalAlign: '-2px', marginRight: 4}}/>
            내용 지우기
          </Button>
          <Button theme="primary" size="sm" disabled={!this.canSend} onClick={() => this.onSubmit()}>
            <FeatherIcon icon={this.form.sendMode === 'schedule' ? 'clock' : 'send'} size="13" style={{verticalAlign: '-2px', marginRight: 4}}/>
            {this.form.sendMode === 'schedule'
              ? `${this.uniqueReceivers.length || 0}명 예약 등록`
              : `${this.uniqueReceivers.length || 0}명에게 발송`}
          </Button>
        </div>

        {this.renderResult()}
      </CardBody>
    );
  }

  renderScheduled() {
    const docs = this.scheduledList.docs || [];
    return (
      <CardBody>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12}}>
          <strong>예약 발송 목록 ({this.scheduledList.totalDocs || 0})</strong>
          <Button size="sm" theme="light" onClick={() => this.loadScheduled()}>
            <FeatherIcon icon="refresh-cw" size="12"/>
          </Button>
        </div>
        <table className="s-table">
          <thead>
            <tr>
              <th>예약 시각</th>
              <th>상태</th>
              <th>수신자</th>
              <th>메시지</th>
              <th>등록자</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 && <tr><td colSpan="6" className="text-center text-muted">예약된 항목이 없습니다.</td></tr>}
            {docs.map(it => (
              <tr key={it._id}>
                <td>{moment(it.sendAt).format('YYYY-MM-DD HH:mm')}</td>
                <td>
                  <span style={{
                    fontSize: 11, fontWeight: 700,
                    color: it.status === 'pending' ? '#2563EB' : it.status === 'sent' ? '#10B981' : it.status === 'canceled' ? '#6B7280' : '#DC2626',
                    background: it.status === 'pending' ? '#EFF6FF' : it.status === 'sent' ? '#ECFDF5' : it.status === 'canceled' ? '#F3F4F6' : '#FEE2E2',
                    padding: '2px 8px', borderRadius: 4
                  }}>{it.status}</span>
                </td>
                <td>{(it.receivers || []).length}명</td>
                <td style={{maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={it.message}>{it.message}</td>
                <td>{it.createdBy}</td>
                <td>
                  {it.status === 'pending' && (
                    <Button size="sm" theme="light" onClick={() => this.cancelScheduled(it)}>
                      <FeatherIcon icon="x" size="11"/>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardBody>
    );
  }

  renderHistory() {
    const docs = this.historyList.docs || [];
    return (
      <CardBody>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12}}>
          <strong>SMS 발송 이력 ({this.historyList.totalDocs || 0})</strong>
          <Button size="sm" theme="light" onClick={() => this.loadHistory()}>
            <FeatherIcon icon="refresh-cw" size="12"/>
          </Button>
        </div>
        <table className="s-table">
          <thead>
            <tr>
              <th>일시</th>
              <th>출처</th>
              <th>상태</th>
              <th>총/성공/실패</th>
              <th>제목/메시지</th>
              <th>실행자</th>
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 && <tr><td colSpan="6" className="text-center text-muted">이력이 없습니다.</td></tr>}
            {docs.map(it => (
              <tr key={it._id}>
                <td>{moment(it.createdAt).format('YYYY-MM-DD HH:mm')}</td>
                <td>{it.topic}</td>
                <td>
                  <span style={{
                    fontSize: 11, fontWeight: 700,
                    color: it.status === 'completed' ? '#10B981' : '#DC2626'
                  }}>{it.status}</span>
                </td>
                <td>{it.totalCount} / <span style={{color: '#10B981'}}>{it.successCount}</span> / <span style={{color: '#DC2626'}}>{it.failCount}</span></td>
                <td style={{maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={it.title}>{it.title}</td>
                <td>{it.sentBy}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardBody>
    );
  }

  renderTemplatesTab() {
    return (
      <CardBody>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12}}>
          <strong>SMS 템플릿 ({this.templates.length})</strong>
          <Button size="sm" theme="light" onClick={() => this.loadTemplates()}>
            <FeatherIcon icon="refresh-cw" size="12"/>
          </Button>
        </div>
        <table className="s-table">
          <thead>
            <tr>
              <th>이름</th>
              <th>발신번호</th>
              <th>타입</th>
              <th>메시지 (미리보기)</th>
              <th>수정일</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {this.templates.length === 0 && <tr><td colSpan="6" className="text-center text-muted">저장된 템플릿이 없습니다.</td></tr>}
            {this.templates.map(t => (
              <tr key={t._id}>
                <td><strong>{t.name}</strong></td>
                <td>{t.sender || '-'}</td>
                <td>{t.msgType || 'AUTO'}</td>
                <td style={{maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={t.message}>{t.message}</td>
                <td>{moment(t.updatedAt || t.createdAt).format('YYYY-MM-DD HH:mm')}</td>
                <td style={{whiteSpace: 'nowrap'}}>
                  <Button size="sm" theme="light" onClick={() => { this.applyTemplate(t); this.setTab('compose'); }}>
                    <FeatherIcon icon="check" size="11"/> 사용
                  </Button>
                  <Button size="sm" theme="light" className="ml-1" onClick={() => this.removeTemplate(t)}>
                    <FeatherIcon icon="trash-2" size="11"/>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardBody>
    );
  }

  render() {
    if (!this.isLogin) {
      return <div className="main-content"><PageTitle/><p className="text-center p-4">로그인이 필요합니다.</p></div>;
    }

    const sm = this.storeModal;
    const tm = this.tplModal;
    const mobileItems = sm.items.filter(it => isMobilePhone(it.tel));
    const nonMobileCount = sm.items.length - mobileItems.length;
    const allOn = mobileItems.length > 0 && mobileItems.every(it => sm.selectedIds[it.tel]);

    return (
      <div className="main-content">
        <PageTitle/>
        <Container fluid>
          <Card className="mt-3">
            <CardHeader>
              <h4 className="m-0">
                <FeatherIcon icon="message-square" size="18" style={{verticalAlign: '-3px', marginRight: 8}}/>
                알리고 문자 발송
              </h4>
              <p className="m-0 mt-1" style={{fontSize: 12, color: '#6B7280'}}>
                알리고 SMS API를 통한 즉시/예약 발송, 발송 이력, 템플릿 관리
              </p>
            </CardHeader>
            {this.renderTabs()}
            {this.activeTab === 'compose' && this.renderCompose()}
            {this.activeTab === 'scheduled' && this.renderScheduled()}
            {this.activeTab === 'history' && this.renderHistory()}
            {this.activeTab === 'templates' && this.renderTemplatesTab()}
          </Card>
        </Container>

        {/* 스토어에서 가져오기 모달 */}
        <Modal size="lg" open={sm.isOpen} toggle={() => {}} centered>
          <ModalHeader>스토어 수신번호 가져오기</ModalHeader>
          <ModalBody>
            <Row className="mb-2">
              <Col md="6">
                <label style={{fontSize: 12, fontWeight: 600}}>요금제 필터</label>
                <FormSelect value={sm.filterType}
                  onChange={e => { this.storeModal.filterType = e.target.value; this.fetchStoreTel(); }}>
                  <option value="all">전체</option>
                  <option value="paid">유료(이용중)</option>
                  <option value="expired">만료</option>
                  <option value="free">무료</option>
                </FormSelect>
              </Col>
              <Col md="6" style={{display: 'flex', alignItems: 'flex-end', gap: 6}}>
                <Button size="sm" theme="light" onClick={() => this.toggleAllStoreItems(!allOn)}>
                  {allOn ? '전체 해제' : '모바일 전체 선택'}
                </Button>
                <span style={{marginLeft: 'auto', fontSize: 12, color: '#6B7280'}}>
                  모바일 {mobileItems.length}개
                  {nonMobileCount > 0 && <span style={{color: '#9CA3AF'}}> · 비모바일 {nonMobileCount}개</span>}
                  {' · '}선택 {Object.values(sm.selectedIds).filter(Boolean).length}개
                </span>
              </Col>
            </Row>
            <div style={{maxHeight: 420, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 6}}>
              {sm.isLoading ? (
                <div className="text-center p-4">조회 중…</div>
              ) : (
                <table className="s-table" style={{margin: 0}}>
                  <thead>
                    <tr><th style={{width: 36}}></th><th>스토어</th><th>대표자</th><th>전화번호</th></tr>
                  </thead>
                  <tbody>
                    {sm.items.length === 0 && <tr><td colSpan="4" className="text-center text-muted p-3">결과가 없습니다.</td></tr>}
                    {sm.items.map(it => {
                      const mobile = isMobilePhone(it.tel);
                      return (
                        <tr key={it.tel}
                            onClick={() => mobile && this.toggleStoreItem(it.tel)}
                            style={{
                              cursor: mobile ? 'pointer' : 'not-allowed',
                              background: sm.selectedIds[it.tel] ? '#EFF6FF' : (mobile ? 'transparent' : '#F9FAFB'),
                              color: mobile ? 'inherit' : '#9CA3AF',
                            }}
                            title={mobile ? '' : '모바일 번호가 아니어서 발송 대상에서 제외됩니다.'}>
                          <td>
                            <input type="checkbox"
                                   disabled={!mobile}
                                   checked={!!sm.selectedIds[it.tel]}
                                   onChange={() => this.toggleStoreItem(it.tel)}/>
                          </td>
                          <td>{it.name || '-'}</td>
                          <td>{it.presidentName || '-'}</td>
                          <td style={{fontFamily: 'monospace'}}>
                            {it.tel}
                            {!mobile && (
                              <span style={{marginLeft: 6, fontSize: 11, color: '#9CA3AF', fontFamily: 'inherit'}}>
                                (비모바일)
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12}}>
              <Button theme="light" size="sm" onClick={() => this.closeStoreModal()}>취소</Button>
              <Button theme="primary" size="sm" onClick={() => this.applyStoreSelection()}>
                선택 항목 추가
              </Button>
            </div>
          </ModalBody>
        </Modal>

        {/* 템플릿 모달 */}
        <Modal size="md" open={tm.isOpen} toggle={() => {}} centered>
          <ModalHeader>{tm.mode === 'save' ? '템플릿으로 저장' : '템플릿 불러오기'}</ModalHeader>
          <ModalBody>
            {tm.mode === 'save' ? (
              <>
                <label style={{fontSize: 12, fontWeight: 600}}>템플릿 이름</label>
                <FormInput placeholder="예) 결제 안내"
                  value={tm.name}
                  onChange={e => { this.tplModal.name = e.target.value; }}/>
                <small style={{color: '#6B7280'}}>현재 입력한 발신번호 / 메시지 / 메시지 타입이 함께 저장됩니다.</small>
                <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12}}>
                  <Button theme="light" size="sm" onClick={() => this.closeTemplateModal()}>취소</Button>
                  <Button theme="primary" size="sm" onClick={() => this.saveCurrentAsTemplate()}>저장</Button>
                </div>
              </>
            ) : (
              <>
                {this.templates.length === 0 ? (
                  <div className="text-center p-3 text-muted">저장된 템플릿이 없습니다.</div>
                ) : (
                  <div style={{maxHeight: 420, overflowY: 'auto'}}>
                    {this.templates.map(t => (
                      <div key={t._id} style={{padding: 10, border: '1px solid #E5E7EB', borderRadius: 6, marginBottom: 8, cursor: 'pointer'}}
                           onClick={() => this.applyTemplate(t)}>
                        <div style={{display: 'flex', justifyContent: 'space-between'}}>
                          <strong>{t.name}</strong>
                          <small style={{color: '#6B7280'}}>{t.msgType || 'AUTO'}</small>
                        </div>
                        <div style={{fontSize: 12, color: '#6B7280', marginTop: 4, whiteSpace: 'pre-wrap', maxHeight: 60, overflow: 'hidden'}}>
                          {t.message}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 12}}>
                  <Button theme="light" size="sm" onClick={() => this.closeTemplateModal()}>닫기</Button>
                </div>
              </>
            )}
          </ModalBody>
        </Modal>

        <ModalLoading isLoading={this.isLoading}/>
      </div>
    );
  }
}

export default AligoSmsSend;
